import type { ApiContext } from "@hobbyshopify/backend-client";
import { BaseController, Rx, type SubtreeModel } from "subtree.js/core";
import type { AuthHandler } from "trunk.js";
import type { AppDeps } from "../../app-deps";
import type { BackendApi } from "../../core/ops/api";
import { failedLoad, idleLoad, type LoadStatus, loaded, loading } from "../../core/primitives/load-status";
import { READ_FAILED_ON_THE_WAY } from "../../core/primitives/review-row";

export class CustomerQrState {
  /** The entry id (shop or `loc_` counter) the dialog is open for, or null when closed. */
  readonly storeId = new Rx<string | null>(null);
  readonly status = new Rx<LoadStatus>(idleLoad);
  /** The customer entry URL: what the QR encodes and what the merchant can paste. */
  readonly url = new Rx<string | null>(null);
}

export abstract class CustomerQrActions {
  abstract open(storeId: string): void;
  abstract close(): void;
  /** Asks for the link again after a read that failed. */
  abstract retry(): void;
}

export type CustomerQrDeps = Pick<AppDeps, "client" | "authHandler">;

/** The shop's customer entry link, for the "Show QR code" button. */
export function loadCustomerLink(o: { client: BackendApi; authHandler: AuthHandler<ApiContext>; storeId: string }) {
  return o.authHandler.callApi((ctx) => o.client.stores.customerLink(ctx, o.storeId));
}

/**
 * The customer QR dialog: a section a page owns for its lifetime, opened per
 * shop. The view renders the code from `url`; the fetch is the only logic here.
 */
export class CustomerQrController extends BaseController implements CustomerQrActions {
  readonly state = new CustomerQrState();
  /** Numbers the link reads, so an older answer or throw never lands over a newer one. */
  #asked = 0;

  constructor(
    subtree: SubtreeModel,
    readonly deps: CustomerQrDeps,
  ) {
    super();
    subtree.put(CustomerQrState, this.state);
    subtree.put(CustomerQrActions, this);
  }

  open = (storeId: string) => {
    this.state.storeId.value = storeId;
    this.state.url.value = null;
    this.state.status.value = loading;
    void this.#load(storeId, ++this.#asked);
  };

  retry = () => {
    const storeId = this.state.storeId.value;
    if (storeId !== null) this.open(storeId);
  };

  close = () => {
    this.state.storeId.value = null;
    this.state.url.value = null;
    this.state.status.value = idleLoad;
  };

  /** Only the newest read shows. One that throws is shown as a refused one is, with Try again, then thrown on. */
  async #load(storeId: string, asked: number): Promise<void> {
    const r = await loadCustomerLink({ ...this.deps, storeId }).catch((thrown: unknown) => {
      if (!this.isDisposed && asked === this.#asked)
        this.state.status.value = { kind: "error", message: READ_FAILED_ON_THE_WAY };
      throw thrown;
    });
    if (this.isDisposed || asked !== this.#asked) return;
    if (!r.ok) {
      this.state.status.value = failedLoad(r.errors);
      return;
    }
    this.state.url.value = r.value;
    this.state.status.value = loaded;
  }
}
